import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import { Checkbox, Classes, Icon, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import apis from 'browser/app/models/apis'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import { DEBOUNCE_TIMEOUT } from 'browser/app/utils/utils'
import { IComponentsContext } from 'browser/contexts/components/components-context'
import ComponentsMap from 'browser/components'
import { UI2EntityPreviewCard } from 'browser/components/ui2/display/ui2-entity-preview-card/ui2-entity-preview-card'
import { ViewRenderer } from 'shared-libs/components/view/renderer'
import { Entity } from 'shared-libs/models/entity'
import { Query } from 'shared-libs/models/query'
import { evaluateExpression, evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { UI2EntitySearchFieldProps } from 'shared-libs/components/ui2/fields/ui2-entity-search-field/ui2-entity-search-field-props'
import { ResolvedSchemaPaths } from 'shared-libs/components/ui2/fields/ui2-entity-search-field/entity-search-utils'
import type { ISearchFilterConfig } from 'shared-libs/helpers/search-filters'

import { EntitySearchFilters } from './entity-search-filters'
import { MobileSearchFilters } from 'browser/mobile/components/entity-search/search-filters'
import './_entity-search-auto-modal.scss'

const NUM_RESULTS = 20
const DEFAULT_SEARCH_ORDER = [{ path: 'creationDate', type: 'descending' }]

interface IEntitySearchAutoModalProps {
  fieldProps: UI2EntitySearchFieldProps
  componentsContext: IComponentsContext
  entitySchema: any
  entityDisplayName: string
  entityDisplayNamePlural: string
  schemaPaths: ResolvedSchemaPaths
  onCommitSelection: (payload: { entity: any; searchFilter: string }) => void
  onCreateEntity?: (searchFilter: string) => void
  onClose?: () => void
  /** Pre-selected entities when opening in multi-select mode. */
  initialSelectedEntityMap?: Map<string, any>
  /** Called with all selected entities when the modal closes in multi-select mode. */
  onCommitMultiSelection?: (entities: any[]) => void
  isMobileWeb?: boolean
  /** Open with the selected-only view already toggled on. */
  initialShowSelectedOnly?: boolean
}

interface IEntitySearchAutoModalState {
  searchFilter: string
  appliedFilters: ISearchFilterConfig[]
  availableFilters: ISearchFilterConfig[]
  entities: Entity[]
  isSearching: boolean
  selectedItem?: Entity
  selectedEntityMap: Map<string, any>
  showSelectedOnly: boolean
}

/**
 * Auto-mode search modal: dark header with search input + filter pills,
 * scrollable results list, optional confirm bar and creatable footer.
 */
export class EntitySearchAutoModal extends React.Component<
  IEntitySearchAutoModalProps,
  IEntitySearchAutoModalState
> {
  private collection: any
  private debouncedQuery: any

  constructor(props: IEntitySearchAutoModalProps) {
    super(props)
    this.debouncedQuery = _.debounce(this.queryEntities, DEBOUNCE_TIMEOUT)
    const initialFilters: ISearchFilterConfig[] = _.isArray(props.fieldProps?.query?.filters)
      ? _.compact(props.fieldProps.query.filters)
      : []
    const initialAvailableFilters: ISearchFilterConfig[] = _.isArray(
      props.fieldProps?.query?.availableFilters
    )
      ? _.compact(props.fieldProps.query.availableFilters)
      : []
    this.state = {
      searchFilter: '',
      appliedFilters: initialFilters,
      availableFilters: initialAvailableFilters,
      entities: [],
      isSearching: false,
      selectedItem: undefined,
      selectedEntityMap: props.initialSelectedEntityMap
        ? new Map(props.initialSelectedEntityMap)
        : new Map(),
      showSelectedOnly: props.initialShowSelectedOnly ?? false,
    }
  }

  public componentDidMount() {
    if (this.isSearchValid(this.state.searchFilter)) {
      this.setState({ isSearching: true })
      this.queryEntities(this.state.searchFilter)
    }
  }

  public componentWillUnmount() {
    this.debouncedQuery.cancel()
    this.collection?.cancel()
    this.collection?.dispose()
  }

  public render() {
    return (
      <div className="ui2-entitySearchAutoModal">
        {this.renderHeader()}
        {this.renderSearchContent()}
        {this.renderPrimaryCreateFooter()}
        {this.renderConfirmBar()}
      </div>
    )
  }

  private renderHeader = () => {
    const { searchFilter, selectedEntityMap, showSelectedOnly } = this.state
    const { searchModalSubtitle } = this.props.fieldProps
    const isMultiSelect = this.isMultiSelectMode()
    const selectedCount = selectedEntityMap.size
    return (
      <header className="ui2-entitySearchAutoModal-header">
        <div className="ui2-entitySearchAutoModal-titleRow">
          <div className="ui2-entitySearchAutoModal-titleBlock">
            <h4 className="ui2-entitySearchAutoModal-title">{this.getHeaderTitle()}</h4>
            {!isMultiSelect && searchModalSubtitle && (
              <div className="ui2-entitySearchAutoModal-subtitle">{searchModalSubtitle}</div>
            )}
          </div>
          <Button
            className={classNames(Classes.MINIMAL, 'ui2-entitySearchAutoModal-closeButton')}
            onClick={this.handleClose}
          >
            <Icon icon={IconNames.CROSS} />
          </Button>
        </div>
        <div className="ui2-entitySearchAutoModal-searchRow">
          <Icon icon={IconNames.SEARCH} className="ui2-entitySearchAutoModal-searchIcon" />
          <input
            autoFocus
            className="ui2-entitySearchAutoModal-searchInput"
            placeholder={`Search ${_.lowerCase(this.props.entityDisplayNamePlural)}...`}
            value={searchFilter}
            onChange={this.handleSearchFilterChange}
          />
          {!_.isEmpty(searchFilter) && (
            <Button
              className={classNames(Classes.MINIMAL, 'ui2-entitySearchAutoModal-clearButton')}
              onClick={this.handleClearSearch}
            >
              <Icon icon={IconNames.CROSS} />
            </Button>
          )}
        </div>
        {this.renderFilterChips()}
      </header>
    )
  }

  private renderViewSelectedToggle = () => {
    const { selectedEntityMap, showSelectedOnly } = this.state
    if (!this.isMultiSelectMode() || selectedEntityMap.size === 0) return null
    return (
      <button
        className="ui2-entitySearchAutoModal-viewSelectedToggle"
        onClick={() => this.setState({ showSelectedOnly: !showSelectedOnly })}
      >
        {showSelectedOnly
          ? '← All results'
          : `${selectedEntityMap.size} ${selectedEntityMap.size === 1 ? 'item' : 'items'} selected · View →`}
      </button>
    )
  }

  private renderFilterChips = () => {
    const { appliedFilters, availableFilters } = this.state
    if (_.isEmpty(appliedFilters) && _.isEmpty(availableFilters)) return null
    const filterProps = {
      filters: appliedFilters,
      availableFilters,
      entitySchema: this.props.entitySchema,
      onFiltersChange: this.handleFiltersChange,
    }
    return (
      <div className="ui2-entitySearchAutoModal-chipsRow">
        {this.props.isMobileWeb
          ? <MobileSearchFilters {...filterProps} />
          : <EntitySearchFilters {...filterProps} />
        }
      </div>
    )
  }

  private renderPrimaryCreateFooter = () => {
    const creatableMode = this.props.fieldProps.creatableMode ?? 'secondary'
    const { entityCreate } = this.props.schemaPaths
    if (creatableMode !== 'primary' || !entityCreate) return null
    return <div className="ui2-entitySearchAutoModal-createFooter">{this.renderCreateAction()}</div>
  }

  private renderSearchContent = () => {
    const { isSearching, showSelectedOnly } = this.state
    const isSelectedView = showSelectedOnly && this.isMultiSelectMode()
    return (
      <div className="ui2-entitySearchAutoModal-body">
        <div key={isSelectedView ? 'selected' : 'search'} className="ui2-entitySearchAutoModal-bodyPane">
          {isSelectedView ? (
            this.renderSelectedOnlyBody()
          ) : isSearching ? (
            <div className="ui2-entitySearchAutoModal-loading">
              <LoadingSpinner />
            </div>
          ) : (
            this.renderSearchBody()
          )}
        </div>
      </div>
    )
  }

  private renderSelectedOnlyBody = () => {
    const { selectedEntityMap } = this.state
    const selectedEntities = Array.from(selectedEntityMap.values())
    if (_.isEmpty(selectedEntities)) {
      return (
        <div className="ui2-entitySearchAutoModal-emptyState">
          <Icon icon={IconNames.SEARCH} className="ui2-entitySearchAutoModal-stateIcon" />
          <div className="ui2-entitySearchAutoModal-stateText">No items selected.</div>
        </div>
      )
    }
    return (
      <>
        <div className="ui2-entitySearchAutoModal-resultsHeaderRow">
          <div className="ui2-entitySearchAutoModal-resultsHeader">
            {`${selectedEntities.length} ${selectedEntities.length === 1 ? 'item' : 'items'} selected`}
          </div>
          <div className="ui2-entitySearchAutoModal-resultsHeaderControls">
            {this.renderViewSelectedToggle()}
          </div>
        </div>
        <div className="ui2-entitySearchAutoModal-results">
          {selectedEntities.map((entity, index) => this.renderResultCard(entity, index))}
        </div>
      </>
    )
  }

  private renderSearchBody = () => {
    const { minQueryLength, preloadResults } = this.props.fieldProps
    const { searchFilter, entities, appliedFilters } = this.state
    const hasAppliedFilters = !_.isEmpty(appliedFilters)

    if (_.isEmpty(searchFilter) && _.isEmpty(entities)) {
      if (preloadResults && hasAppliedFilters) {
        return this.renderEmptyResultState()
      }
      return this.renderHelpState()
    }

    if (!this.isSearchValid(searchFilter)) {
      return (
        <div className="ui2-entitySearchAutoModal-emptyState">
          <Icon icon={IconNames.SEARCH} className="ui2-entitySearchAutoModal-stateIcon" />
          <div className="ui2-entitySearchAutoModal-stateText">
            {`Enter at least ${minQueryLength ?? 3} characters to search.`}
          </div>
        </div>
      )
    }

    if (_.isEmpty(entities)) {
      return this.renderEmptyResultState()
    }

    const creatableMode = this.props.fieldProps.creatableMode ?? 'secondary'

    return (
      <>
        <div className="ui2-entitySearchAutoModal-resultsHeaderRow">
          <div className="ui2-entitySearchAutoModal-resultsHeader">
            {_.isEmpty(searchFilter) ? 'Results' : `Results for "${searchFilter}"`}
          </div>
          {this.isMultiSelectMode() && (
            <div className="ui2-entitySearchAutoModal-resultsHeaderControls">
              {this.renderSelectAllRow()}
              {this.renderViewSelectedToggle()}
            </div>
          )}
        </div>
        <div className="ui2-entitySearchAutoModal-results">
          {_.map(entities, (entity, index) => this.renderResultCard(entity, index))}
        </div>
        {creatableMode === 'secondary' && !this.isMultiSelectMode() && this.renderCreateAction()}
      </>
    )
  }

  private renderHelpState = () => {
    const { searchHelp } = this.props.schemaPaths
    if (searchHelp && _.get(this.props.entitySchema, searchHelp)) {
      return (
        <ViewRenderer
          schema={_.get(this.props.entitySchema, searchHelp)}
          state={this.getRendererState()}
          componentsMap={ComponentsMap}
          api={apis}
        />
      )
    }
    return (
      <div className="ui2-entitySearchAutoModal-emptyState">
        <Icon icon={IconNames.SEARCH} className="ui2-entitySearchAutoModal-stateIcon" />
        <div className="ui2-entitySearchAutoModal-stateText">
          {`Search ${_.lowerCase(this.props.entityDisplayNamePlural)} by ID or keyword.`}
        </div>
      </div>
    )
  }

  private renderEmptyResultState = () => {
    const { searchEmptyResults } = this.props.schemaPaths
    const { searchFilter, appliedFilters } = this.state
    const hasClearableAppliedFilter = _.some(appliedFilters, (f) => f?.isClearable === true)

    if (searchEmptyResults && _.get(this.props.entitySchema, searchEmptyResults)) {
      return (
        <>
          <ViewRenderer
            schema={_.get(this.props.entitySchema, searchEmptyResults)}
            state={this.getRendererState()}
            componentsMap={ComponentsMap}
            api={apis}
          />
          {hasClearableAppliedFilter && (
            <div className="ui2-entitySearchAutoModal-clearFiltersHint">
              Try removing filters to see more results.
            </div>
          )}
        </>
      )
    }

    const primaryText = _.isEmpty(searchFilter)
      ? 'No results match your filters.'
      : `No results found for "${searchFilter}".`

    return (
      <div className="ui2-entitySearchAutoModal-emptyState">
        <Icon icon={IconNames.SMALL_CROSS} className="ui2-entitySearchAutoModal-stateIcon" />
        <div className="ui2-entitySearchAutoModal-stateText">{primaryText}</div>
        {hasClearableAppliedFilter && (
          <div className="ui2-entitySearchAutoModal-clearFiltersHint">
            Try removing filters to see more results.
          </div>
        )}
        {this.renderCreateAction()}
      </div>
    )
  }

  private renderCreateAction = () => {
    const { entityCreate } = this.props.schemaPaths
    if (!entityCreate) return null
    const creatableMode = this.props.fieldProps.creatableMode ?? 'secondary'
    if (creatableMode === 'secondary') {
      return (
        <div className="ui2-entitySearchAutoModal-createAction ui2-entitySearchAutoModal-createAction--secondary">
          <div className="ui2-entitySearchAutoModal-createHelp">
            {`Don't see the ${_.lowerCase(this.props.entityDisplayName)} you're looking for?`}
          </div>
          <Button
            className={classNames(Classes.MINIMAL, 'ui2-entitySearchAutoModal-createLink')}
            onClick={this.handleCreateClick}
          >
            {`Create new ${this.props.entityDisplayName}`}
          </Button>
        </div>
      )
    }
    return (
      <div className="ui2-entitySearchAutoModal-createAction">
        <Button intent={Intent.PRIMARY} icon={IconNames.PLUS} onClick={this.handleCreateClick}>
          {`Create new ${this.props.entityDisplayName}`}
        </Button>
      </div>
    )
  }

  private renderResultCard = (entity: Entity, index: number) => {
    const { selectedItem } = this.state
    const { fieldProps } = this.props
    const isSelected = selectedItem?.uniqueId === entity?.uniqueId
    const selectOption = () => this.handleSelectEntity(entity)

    const { entitySearchResultCard } = this.props.schemaPaths
    const schema = entitySearchResultCard && _.get(this.props.entitySchema, entitySearchResultCard)
    let content: React.ReactNode

    if (schema && schema.type === 'ui:entityPreviewCard') {
      const { type: _type, ...cardPropsFromSchema } = schema
      content = (
        <UI2EntityPreviewCard
          entity={entity}
          {...cardPropsFromSchema}
          isDisabled={true}
          showTypeStripe={false}
        />
      )
    } else {
      if (schema) {
        // eslint-disable-next-line no-console
        console.warn(
          `[UI2EntitySearchField] entitySearchResultCardSchemaPath "${entitySearchResultCard}" is ` +
            `not a ui:form:entityPreviewCard. Falling back to displayName card.`
        )
      }
      const optionValuePath = fieldProps.optionValuePath ?? 'displayName'
      content = (
        <UI2EntityPreviewCard
          entity={entity}
          title={_.get(entity, optionValuePath) ?? entity.displayName}
          isDisabled={true}
          showTypeStripe={false}
        />
      )
    }

    if (this.isMultiSelectMode()) {
      const isChecked = this.state.selectedEntityMap.has(entity?.uniqueId)
      return (
        <div
          key={entity?.uniqueId || index}
          className={classNames(
            'ui2-entitySearchAutoModal-resultCard',
            'ui2-entitySearchAutoModal-resultCard--multiSelect',
            { 'ui2-entitySearchAutoModal-resultCard--checked': isChecked }
          )}
          onClick={selectOption}
        >
          <div className="ui2-entitySearchAutoModal-resultCard-checkboxWrapper">
            <Checkbox checked={isChecked} onChange={_.noop} />
          </div>
          <div className="ui2-entitySearchAutoModal-resultCard-cardContent">{content}</div>
        </div>
      )
    }

    return (
      <div
        key={entity?.uniqueId || index}
        className={classNames('ui2-entitySearchAutoModal-resultCard', {
          'ui2-entitySearchAutoModal-resultCard--selected': isSelected,
        })}
        onClick={selectOption}
      >
        {content}
      </div>
    )
  }

  private renderConfirmBar = () => {
    if (this.isMultiSelectMode()) {
      const count = this.state.selectedEntityMap.size
      if (count === 0) return null
      return (
        <div className="ui2-entitySearchAutoModal-confirmBar">
          <Button
            className="ui2-entitySearchAutoModal-confirmButton"
            intent={Intent.PRIMARY}
            onClick={this.handleMultiSelectConfirm}
          >
            {`Confirm ${count} ${count === 1 ? 'Selection' : 'Selections'}`}
          </Button>
        </div>
      )
    }

    const { confirmOnSelect } = this.props.fieldProps
    const { selectedItem, entities, isSearching, searchFilter } = this.state
    const hasVisibleResults =
      !isSearching && !_.isEmpty(entities) && this.isSearchValid(searchFilter)
    if (!confirmOnSelect || !hasVisibleResults) return null
    return (
      <div className="ui2-entitySearchAutoModal-confirmBar">
        <Button
          className="ui2-entitySearchAutoModal-confirmButton"
          intent={Intent.PRIMARY}
          isDisabled={_.isNil(selectedItem)}
          onClick={this.handleConfirmSelection}
        >
          {this.resolveConfirmLabel()}
        </Button>
      </div>
    )
  }

  // ─── Handlers ────────────────────────────────────────────────────────────────

  private handleSearchFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchFilter = event.target.value
    this.setState({ searchFilter, selectedItem: undefined })

    this.collection?.cancel()

    if (!this.isSearchValid(searchFilter)) {
      this.debouncedQuery.cancel()
      this.setState({ entities: [], isSearching: false })
      return
    }

    this.setState({ isSearching: true })
    this.debouncedQuery(searchFilter)
  }

  private handleClearSearch = () => {
    this.setState({ searchFilter: '', entities: [], isSearching: false, selectedItem: undefined })
    this.debouncedQuery.cancel()
    this.collection?.cancel()
  }

  private handleFiltersChange = (
    filters: ISearchFilterConfig[],
    availableFilters?: ISearchFilterConfig[]
  ) => {
    this.setState(
      {
        appliedFilters: filters,
        availableFilters: availableFilters ?? this.state.availableFilters,
      },
      () => {
        this.props.fieldProps.onFiltersChange?.(filters, availableFilters)
        if (this.isSearchValid(this.state.searchFilter)) {
          this.setState({ isSearching: true })
          this.queryEntities(this.state.searchFilter)
        }
      }
    )
  }

  private handleSelectEntity = async (entity: Entity) => {
    if (this.isMultiSelectMode()) {
      this.setState((prev) => {
        const newMap = new Map(prev.selectedEntityMap)
        if (newMap.has(entity?.uniqueId)) {
          newMap.delete(entity?.uniqueId)
        } else {
          newMap.set(entity?.uniqueId, entity)
        }
        return { selectedEntityMap: newMap }
      })
      return
    }
    if (this.props.fieldProps.confirmOnSelect) {
      this.setState({ selectedItem: entity })
      return
    }
    this.setState({ selectedItem: entity })
    const refreshed = await this.refreshEntity(entity)
    this.props.onCommitSelection({ entity: refreshed, searchFilter: this.state.searchFilter })
    this.props.onClose?.()
  }

  private handleSelectAll = () => {
    const { entities } = this.state
    const allSelected = entities.every((e) => this.state.selectedEntityMap.has(e?.uniqueId))
    this.setState((prev) => {
      const newMap = new Map(prev.selectedEntityMap)
      if (allSelected) {
        entities.forEach((e) => newMap.delete(e?.uniqueId))
      } else {
        entities.forEach((e) => newMap.set(e?.uniqueId, e))
      }
      return { selectedEntityMap: newMap }
    })
  }

  private renderSelectAllRow = () => {
    const { entities } = this.state
    if (_.isEmpty(entities)) return null
    const allSelected = entities.every((e) => this.state.selectedEntityMap.has(e?.uniqueId))
    return (
      <div
        className={classNames('ui2-entitySearchAutoModal-selectAllRow', {
          'ui2-entitySearchAutoModal-selectAllRow--checked': allSelected,
        })}
        onClick={this.handleSelectAll}
      >
        <Checkbox
          checked={allSelected}
          onChange={_.noop}
          label={allSelected ? 'Deselect All' : 'Select All'}
        />
      </div>
    )
  }

  private isMultiSelectMode = () => {
    return (
      this.props.onCommitMultiSelection !== undefined &&
      this.props.fieldProps.isMultiSelect === true &&
      (this.props.fieldProps.searchMode ?? 'auto') !== 'submit'
    )
  }

  private handleConfirmSelection = async () => {
    const { selectedItem, searchFilter } = this.state
    if (_.isNil(selectedItem)) return
    const refreshed = await this.refreshEntity(selectedItem)
    this.props.onCommitSelection({ entity: refreshed, searchFilter })
    this.props.onClose?.()
  }

  private handleClose = () => {
    this.props.onClose?.()
  }

  private handleMultiSelectConfirm = () => {
    this.props.onCommitMultiSelection?.(Array.from(this.state.selectedEntityMap.values()))
    this.props.onClose?.()
  }

  private handleCreateClick = () => {
    this.props.onCreateEntity?.(this.state.searchFilter)
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  private queryEntities = async (searchFilter: string = '') => {
    const { query = {}, transformSearchFilterFn, transformResultsFn } = this.props.fieldProps
    const { entityType, queryPath, queryPaths, queryType } = query

    if (!entityType) {
      this.setState({ entities: [], isSearching: false })
      return
    }

    const queryInput = transformSearchFilterFn
      ? transformSearchFilterFn(searchFilter)
      : searchFilter

    const queryBuilder = new Query(apis)
      .setEntityType(entityType)
      .setQueryType(queryType)
      .setComputations(query?.computations)
      .setGroups(query?.groups)
      .setFilters(this.state.appliedFilters)
      .setMetadata(query?.metadata)
      .setSize(NUM_RESULTS)
      .setOrders(query?.orders || DEFAULT_SEARCH_ORDER)
      .setQuery(queryInput)

    if (queryPaths) {
      queryBuilder.setQueryPaths(queryPaths)
    } else {
      queryBuilder.setQueryPath(queryPath)
    }

    this.collection?.cancel()
    this.collection?.dispose()
    this.collection = queryBuilder.getCollection()

    try {
      let entities = await this.collection.find()
      if (transformResultsFn) {
        entities = await Promise.resolve(
          transformResultsFn({ entities, searchQuery: searchFilter })
        )
      }
      this.setState({ entities })
    } catch (e) {
      if (this.isCancellationError(e)) return
      // eslint-disable-next-line no-console
      console.error('[UI2EntitySearchField] auto-modal query failed', { error: e, searchFilter })
      this.setState({ entities: [] })
    } finally {
      this.collection = undefined
      this.setState({ isSearching: false })
    }
  }

  private refreshEntity = async (entity: Entity) => {
    if (_.isNil(entity?.uniqueId)) return entity
    try {
      return await apis.getStore().findRecord(entity.uniqueId)
    } catch {
      return entity
    }
  }

  private isSearchValid = (searchFilter: string) => {
    const { minQueryLength = 0, preloadResults } = this.props.fieldProps
    return preloadResults || (!_.isEmpty(searchFilter) && searchFilter.length >= minQueryLength)
  }

  private isCancellationError = (error: any) =>
    error?.name === 'CancellationError' ||
    error?.message?.includes('canceled') ||
    error?.message?.includes('cancelled') ||
    error?.message?.includes('aborted')

  private resolveConfirmLabel = (): string => {
    const { confirmButtonLabel, frames } = this.props.fieldProps as any
    const { selectedItem, searchFilter } = this.state
    const fallback = `Select ${this.props.entityDisplayName}`
    if (!confirmButtonLabel) return fallback
    const selectedData = selectedItem
      ? { ...selectedItem.content, displayName: selectedItem.displayName }
      : undefined
    try {
      const scope = {
        ...CustomFormulas,
        selectedItem: selectedData,
        item: selectedData,
        searchFilter,
      }
      const result = frames
        ? evaluateExpressionWithScopes(frames, confirmButtonLabel, scope)
        : evaluateExpression({ ...this.props.componentsContext, ...scope }, confirmButtonLabel)
      return _.isNil(result) || result === '' ? fallback : String(result)
    } catch {
      return fallback
    }
  }

  private getHeaderTitle = () => {
    const { searchModalTitle } = this.props.fieldProps
    if (searchModalTitle) return searchModalTitle
    return `Find ${_.capitalize(this.props.entityDisplayName)}`
  }

  private getRendererState = () => ({
    ...this.props.componentsContext,
    ...this.props.fieldProps,
    entities: this.state.entities,
    searchFilter: this.state.searchFilter,
    entityDisplayName: this.props.entityDisplayName,
  })
}
